import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import { useToast } from "hooks/useToast";
import Button from "components/lib/Shared/Button";
import Input from "components/lib/Shared/Input";
import Checkbox from "components/lib/Shared/Checkbox";
import { getFullName } from "utils";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import { applyProRataAsync } from "state/slices/shareCapitalAllotment";
import {
  hasManualAllocation,
  clearManualAllocation,
  saveBeneficialOwnershipChanges,
} from "state/slices/manualShareAllocation";
import MainShareAllocationComponent from "../../ShareAllocationComponents";
import OfficialsBeneficialOwnershipComponent from "../../BeneficialOwnershipComponents/OfficialsBeneficialOwnershipComponent";
import ShareholderPercentageAlert from "./ShareholderPercentageAlert";
import {
  calculateShareholdingPercentages,
  calculateShareholdingPercentagesCrossTable,
  compareShareholdingPercentages,
  getShareholdersFromOfficials,
  debugPercentageCalculations,
} from "utils/shareholdingCalculations";

const ShareChangesSubRow = (props) => {
  const {
    row,
    transaction,
    shareCapitalChange,
    visibleColumns = [],
    onAllocationSuccess,
    onPercentageChanges,
    initiatedProcessType,
    remainderAmounts,
    idType,
    disableForm = false,
    isAccessAllowed = true,
    mode = { editing: false, viewing: false },
    isReportable = false,
    // NEW: Data from both tables for cross-table calculation
    currentAppointmentsData,
    changesListData,
    boOnlyView = false,
    onBOUpdateSuccess,
  } = props;

  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.userSlice);
  const { toast } = useToast(5000);
  const { companyId } = useParams();

  const isManualTransaction = transaction?.transactionType === "MANUAL";

  const originalCompanyOfficials = useSelector(
    (state) => state.companyOfficialsSlice.companyOfficials.items
  );

  // Access company share capital for percentage calculations
  const { shares: nominalShareCapital } = useSelector(
    (state) => state.sharesTableSlice
  );

  const votesPerShareByClass = useMemo(() => {
    const map = {};
    if (Array.isArray(nominalShareCapital)) {
      nominalShareCapital.forEach((share) => {
        if (share.sharesCategory) {
          map[share.sharesCategory] = Number(share.votesPerShare || 0);
        }
      });
    }
    return map;
  }, [nominalShareCapital]);

  // State for tracking shareholding percentage changes
  const [originalPercentages, setOriginalPercentages] = useState(null);
  const [percentageChanges, setPercentageChanges] = useState(null);
  const [showPercentageAlert, setShowPercentageAlert] = useState(false);

  // Capture original percentages when component mounts or when key data changes
  useEffect(() => {
    if (isManualTransaction && originalCompanyOfficials.length > 0) {
      captureOriginalPercentages();

      // Test the calculation logic with sample data
      debugPercentageCalculations();
    }
  }, [isManualTransaction, originalCompanyOfficials, transaction]);

  const colSpan =
    Array.isArray(visibleColumns) && visibleColumns.length > 0
      ? visibleColumns.length
      : 12;

  // Common refs
  const officialId = row?.original?._id;
  const fullName =
    row?.original?.fullName ||
    getFullName(row?.original) ||
    getFullName(row?.original?.names) ||
    "Unknown";

  const shareChange = useMemo(
    () => shareCapitalChange || transaction?.shareCapitalChanges?.[0] || {},
    [shareCapitalChange, transaction?.shareCapitalChanges]
  );

  const currentAllocations = useMemo(
    () =>
      Array.isArray(shareChange?.allocations) ? shareChange.allocations : [],
    [shareChange?.allocations]
  );

  const involvedIds = useMemo(
    () =>
      new Set(
        currentAllocations
          .map((a) => [a?.officialId, a?.transfereeId, a?.transferorId])
          .flat()
          .filter(Boolean)
          .map(String)
      ),
    [currentAllocations]
  );

  const classOfShares = shareChange?.classOfShares || "";

  // All shareholders (eligible pool)
  const shareholders = useMemo(() => {
    return originalCompanyOfficials
      .filter((o) =>
        ["Shareholder", "Joint Shareholder", "Director Shareholder"].includes(
          o?.designation
        )
      )
      .map((o) => {
        const name =
          getFullName(o) || o?.names?.companyName || o?.fullName || "Unknown";
        const clsEntry = Array.isArray(o?.sharesAllotted)
          ? o.sharesAllotted.find((s) => s.classOfShares === classOfShares)
          : null;
        const currentShares = clsEntry
          ? Number(clsEntry.allottedShares || 0)
          : 0;
        return {
          id: o?._id,
          name,
          currentShares,
        };
      });
  }, [originalCompanyOfficials, classOfShares]);

  const transferor = useMemo(
    () =>
      shareholders.find((s) => String(s.id) === String(officialId)) || {
        id: officialId,
        name: fullName,
        currentShares: 0,
      },
    [shareholders, officialId, fullName]
  );

  // Check if there's saved allocation data in Redux
  const hasReduxAllocationData = useSelector((state) =>
    hasManualAllocation(state, companyId, officialId, transaction?._id)
  );

  // Manual allotment state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shareAllocationComplete, setShareAllocationComplete] = useState(false);
  const [localTransactionData, setLocalTransactionData] = useState(null);
  const [isProcessingAllotment, setIsProcessingAllotment] = useState(false);

  // Determine form visibility based on Redux data
  const [showAllocationForm, setShowAllocationForm] = useState(
    !hasReduxAllocationData
  );

  // Sync form visibility when Redux data changes
  useEffect(() => {
    setShowAllocationForm(!hasReduxAllocationData);
    if (hasReduxAllocationData || boOnlyView) {
      setShareAllocationComplete(true);
    }
  }, [hasReduxAllocationData, boOnlyView]);

  // const remainder = Number(shareChange?.remainder ?? 0);
  const manualOptionIsManual =
    String(shareChange?.option || "").toLowerCase() === "manual";

  // Share-Transfer state
  const [transferAmount, setTransferAmount] = useState("");
  const [rightsWaived, setRightsWaived] = useState(false);
  const [specificTransfereeId, setSpecificTransfereeId] = useState("");

  const eligibleTransferees = useMemo(
    () =>
      shareholders.filter(
        (s) =>
          String(s.id) !== String(transferor.id) &&
          !involvedIds.has(String(s.id))
      ),
    [shareholders, transferor.id, involvedIds]
  );

  // Utilities
  const findTargetChangeIndex = (changes) => {
    const byAuth =
      shareChange?.AuthorizationId &&
      ((c) =>
        String(c?.AuthorizationId) === String(shareChange.AuthorizationId));
    const byClass =
      !shareChange?.AuthorizationId &&
      ((c) => c?.classOfShares === shareChange?.classOfShares);
    return changes.findIndex((c) =>
      shareChange?.AuthorizationId ? byAuth(c) : byClass(c)
    );
  };

  // Function to capture original shareholding percentages before allocation
  const captureOriginalPercentages = () => {
    try {
      // Get current appointments shareholders (those still in the main table)
      const currentAppointmentsShareholders = getShareholdersFromOfficials(
        originalCompanyOfficials
      );

      // For original calculation, there should be no changes list yet (everyone is in current appointments)
      const changeListShareholders = [];
      const originalShareCapitalChanges = []; // No allocations in original state

      const originalCalc = calculateShareholdingPercentagesCrossTable(
        currentAppointmentsShareholders,
        changeListShareholders,
        originalShareCapitalChanges,
        votesPerShareByClass
      );

      setOriginalPercentages(originalCalc);
    } catch (error) {
      console.error("Error capturing original percentages:", error);
    }
  };

  // Function to calculate new percentages and compare with original
  const calculateAndComparePercentages = (updatedShareCapitalChanges) => {
    try {
      // Debug the updatedShareCapitalChanges structure in detail
      if (Array.isArray(updatedShareCapitalChanges)) {
        updatedShareCapitalChanges.forEach((change, index) => {});
      }

      // Now we need to split shareholders between current appointments and changes list
      // After allocation, some shareholders move to changes list

      // Get all allocated shareholder IDs
      const allocatedShareholderIds = new Set();
      updatedShareCapitalChanges.forEach((change) => {
        if (Array.isArray(change.allocations)) {
          change.allocations.forEach((allocation) => {
            if (Number(allocation.allocatedShares || 0) > 0) {
              allocatedShareholderIds.add(String(allocation.officialId));
            }
          });
        }
      });

      // Split shareholders based on allocation status
      const allShareholders = getShareholdersFromOfficials(
        originalCompanyOfficials
      );
      const currentAppointmentsShareholders = allShareholders.filter(
        (sh) => !allocatedShareholderIds.has(String(sh.id))
      );
      const changeListShareholders = allShareholders.filter((sh) =>
        allocatedShareholderIds.has(String(sh.id))
      );

      const newPercentages = calculateShareholdingPercentagesCrossTable(
        currentAppointmentsShareholders,
        changeListShareholders,
        updatedShareCapitalChanges,
        votesPerShareByClass
      );

      // Show a sample of original vs new for debugging
      if (originalPercentages && newPercentages) {
        const firstKey = Object.keys(originalPercentages)[0];
        if (firstKey) {
        }
      }

      // Quick test: Check if ANY allocation was processed
      let totalAllocations = 0;
      updatedShareCapitalChanges.forEach((change) => {
        if (Array.isArray(change.allocations)) {
          totalAllocations += change.allocations.reduce(
            (sum, alloc) => sum + Number(alloc.allocatedShares || 0),
            0
          );
        }
      });

      if (totalAllocations === 0) {
        // If no allocations have been made yet, do not show alerts or calculate changes
        // This prevents the alert from showing immediately upon opening the subrow
        setPercentageChanges({});
        setShowPercentageAlert(false);
        if (typeof onPercentageChanges === "function") {
          onPercentageChanges({});
        }
        return {};
      }

      if (originalPercentages) {
        const changes = compareShareholdingPercentages(
          originalPercentages,
          newPercentages
        );

        setPercentageChanges(changes);
        setShowPercentageAlert(Object.keys(changes).length > 0);

        // Notify parent component about percentage changes
        if (typeof onPercentageChanges === "function") {
          onPercentageChanges(changes);
        }

        return changes;
      } else {
        console.warn(
          "Cannot compare percentages - originalPercentages is null. Trying to calculate original now..."
        );

        // Try to calculate original percentages now as fallback
        const originalShareCapitalChanges = Array.isArray(
          transaction?.shareCapitalChanges
        )
          ? transaction.shareCapitalChanges
          : [];

        const fallbackOriginalPercentages = calculateShareholdingPercentages(
          shareholders,
          nominalShareCapital,
          originalShareCapitalChanges
        );

        const changes = compareShareholdingPercentages(
          fallbackOriginalPercentages,
          newPercentages
        );

        setPercentageChanges(changes);
        setShowPercentageAlert(Object.keys(changes).length > 0);

        // Notify parent component about percentage changes
        if (typeof onPercentageChanges === "function") {
          onPercentageChanges(changes);
        }

        return changes;
      }
    } catch (error) {
      console.error("Error calculating percentage changes:", error);
    }

    return {};
  };

  // Calculate percentage changes when originalPercentages are available and transaction has data
  // This ensures that when opening the form (e.g. Adjust BO Distribution), we show the correct "Before/After" values
  useEffect(() => {
    if (originalPercentages && transaction?.shareCapitalChanges) {
      calculateAndComparePercentages(transaction.shareCapitalChanges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalPercentages, transaction?.shareCapitalChanges]);

  // Utility function to sanitize beneficial ownership form data
  // const sanitizeBeneficialOwnershipForm = (formData) => {
  //   if (!formData) return null;

  //   // Create a copy to avoid mutating the original
  //   const sanitized = { ...formData };

  //   // Define enum fields that should be "yes", "no", or null/undefined (not empty strings)
  //   const enumFields = [
  //     "directRightRemoveDirector",
  //     "indirectRightRemoveDirector",
  //     "directCompanyControlRight",
  //     "indirectCompanyControlRight",
  //   ];

  //   // Sanitize enum fields - convert empty strings to "no" (default value)
  //   enumFields.forEach((field) => {
  //     if (
  //       sanitized[field] === "" ||
  //       sanitized[field] === null ||
  //       sanitized[field] === undefined
  //     ) {
  //       sanitized[field] = "no";
  //     }
  //     // Ensure values are lowercase for consistency
  //     if (typeof sanitized[field] === "string") {
  //       sanitized[field] = sanitized[field].toLowerCase();
  //     }
  //   });

  //   // Clean up any other empty string fields that should be null or have default values
  //   Object.keys(sanitized).forEach((key) => {
  //     if (sanitized[key] === "") {
  //       // For percentage fields, convert empty string to 0
  //       if (key.includes("Percent") || key.includes("maxPercent")) {
  //         sanitized[key] = 0;
  //       }
  //       // For date fields, convert empty string to null
  //       else if (key.includes("Date") || key.includes("date")) {
  //         sanitized[key] = null;
  //       }
  //       // For other string fields, keep as empty string or convert to null based on field
  //       else if (!enumFields.includes(key)) {
  //         // Keep as empty string for text fields that can be empty
  //         // This is safer than converting to null
  //       }
  //     }
  //   });

  //   return sanitized;
  // };

  const persistShareCapitalChanges = async (updatedShareCapitalChanges) => {
    // Beneficial ownership details are now included within each allocation
    // No need to sanitize separately as it's handled within the allocation

    const updatePayload = {
      companyId,
      id: transaction?._id,
      documentId: transaction?._id, // Add documentId as transaction ID
      formName: "initiateTransactionChange",
      shareCapitalChanges: updatedShareCapitalChanges,
      // No global beneficialOwnershipDetails as they're now in allocations
    };

    const editResult = await handleCompanyMaintenanceFormData(
      "existing-request",
      updatePayload,
      companyId,
      officialId
    );

    const ok =
      editResult?.type ===
        "transactionChange/editInitiatedTransactionChangeAsync/fulfilled" ||
      editResult?.payload?.status === true;

    if (!ok) {
      const msg =
        editResult?.payload?.messageText ||
        editResult?.payload?.message ||
        "Failed to update transaction.";
      console.error("=== PERSIST SHARE CAPITAL CHANGES FAILED ===");
      console.error("Error message:", msg);
      console.error("Full edit result:", editResult);
      throw new Error(msg);
    }

    // Fetch updated transaction data
    const refreshResult = await dispatch(
      getAllInitiateTransactionChangeAsync({
        companyId,
        params: { transactionType: "MANUAL" },
      })
    );

    return editResult;
  };

  // Handle share allocation completion
  const handleShareAllocationComplete = (updatedTransactionData) => {
    setShareAllocationComplete(true);
    setLocalTransactionData(updatedTransactionData);
    setShowAllocationForm(false); // Hide form, show table

    // Force a small delay to ensure state updates are processed
    setTimeout(() => {
      setShowAllocationForm(false);
    }, 100);

    // Ensure beneficial ownership component reflects the updated share allocation
    // The OfficialsBeneficialOwnershipComponent will automatically recalculate percentages
    // based on the updated sharesAllotted form field
  };

  // Handle edit allocation data
  const handleEditAllocation = () => {
    setShowAllocationForm(true);
    setShareAllocationComplete(false);
  };

  // Handle clear allocation data
  // const handleClearAllocation = () => {
  //   if (window.confirm("Are you sure you want to clear the allocation data?")) {
  //     dispatch(
  //       clearManualAllocation({
  //         companyId,
  //         officialId,
  //         transactionId: transaction?._id,
  //       })
  //     );
  //     setShowAllocationForm(true);
  //     setShareAllocationComplete(false);
  //     setLocalTransactionData(null);
  //     toast("success", "Allocation data cleared successfully.");
  //   }
  // };

  // Handle final submission of allotment and BO (following pro-rata pattern)
  const handleProcessAllotment = async (values) => {
    setIsProcessingAllotment(true);

    try {
      const changes = Array.isArray(transaction?.shareCapitalChanges)
        ? [...transaction.shareCapitalChanges]
        : [];

      const targetIdx = findTargetChangeIndex(changes);
      if (targetIdx < 0) {
        throw new Error("Target share change record not found in transaction.");
      }

      const change = changes[targetIdx] || {};

      // Handle BO Only View - Ensure we have transaction data even if allocation form was skipped
      let transactionDataToUse = localTransactionData;

      if (boOnlyView && !transactionDataToUse) {
        const existingAlloc = Array.isArray(change.allocations)
          ? change.allocations.find(
              (a) => String(a.officialId) === String(officialId)
            )
          : null;

        if (existingAlloc) {
          transactionDataToUse = {
            allocations: change.allocations,
            allottedShares: existingAlloc.allocatedShares,
            classOfShares: change.classOfShares,
            sharePrice: existingAlloc.sharePrice,
            sharePremium: existingAlloc.sharePremium,
            paidUpShares: existingAlloc.paidUpShares,
            effectiveDate: existingAlloc.effectiveDate,
          };
        } else {
          // Construct nil allocation data for BO update
          transactionDataToUse = {
            allocations: change.allocations || [],
            allottedShares: 0,
            classOfShares: change.classOfShares || shareChange?.classOfShares,
            sharePrice: 0,
            sharePremium: 0,
            paidUpShares: 0,
            effectiveDate: new Date(),
          };
        }
      }

      const existing = Array.isArray(change?.allocations)
        ? [...change.allocations]
        : [];
      let newAllocations = [...existing];
      const idx = newAllocations.findIndex(
        (a) => String(a?.officialId) === String(officialId)
      );

      const official = row?.original || {};
      const fullNameForAlloc =
        getFullName(official) ||
        official?.names?.companyName ||
        fullName ||
        "Unknown";

      const currentShares = Array.isArray(row?.original?.sharesAllotted)
        ? row.original.sharesAllotted
            .filter(
              (share) =>
                share.classOfShares === transactionDataToUse?.classOfShares
            )
            .reduce((sum, share) => sum + Number(share.allottedShares || 0), 0)
        : 0;

      const votingRights = Array.isArray(row?.original?.sharesAllotted)
        ? row.original.sharesAllotted
            .filter(
              (share) =>
                share.classOfShares === transactionDataToUse?.classOfShares
            )
            .reduce((sum, share) => sum + Number(share.votingRights || 0), 0)
        : 0;

      const allocatedShares = Number(transactionDataToUse?.allottedShares || 0);
      const proportionDenominator = Number(change?.sharesOnOffer || 0);
      const proportion =
        proportionDenominator > 0
          ? (allocatedShares / proportionDenominator).toFixed(6).toString()
          : "0.000000";

      const originalBOForm = row?.original?.beneficialOwnershipForm || {};
      const currentBeneficialOwnership = {
        maxPercentShareholding: Number(
          originalBOForm.maxPercentShareholding || 0
        ),
        maxPercentVotingRights: Number(
          originalBOForm.maxPercentVotingRights || 0
        ),
        directPercentShareholding:
          originalBOForm.directPercentShareholding || "",
        indirectPercentShareholding:
          originalBOForm.indirectPercentShareholding || "",
        directPercentVotingRights:
          originalBOForm.directPercentVotingRights || "",
        indirectPercentVotingRights:
          originalBOForm.indirectPercentVotingRights || "",
        directRightRemoveDirector:
          originalBOForm.directRightRemoveDirector || "no",
        indirectRightRemoveDirector:
          originalBOForm.indirectRightRemoveDirector || "no",
        directCompanyControlRight:
          originalBOForm.directCompanyControlRight || "no",
        indirectCompanyControlRight:
          originalBOForm.indirectCompanyControlRight || "no",
        dateOfBecomingBO: originalBOForm.dateOfBecomingBO || "",
        sourceOfBOInformation: originalBOForm.sourceOfBOInformation || "",
        resolvedFields: row?.original?.resolvedFields || [],
      };

      const updatedBeneficialOwnership = {
        maxPercentShareholding: Number(
          values.beneficialOwnershipForm?.maxPercentShareholding || 0
        ),
        maxPercentVotingRights: Number(
          values.beneficialOwnershipForm?.maxPercentVotingRights || 0
        ),
        directPercentShareholding:
          values.beneficialOwnershipForm?.directPercentShareholding || "",
        indirectPercentShareholding:
          values.beneficialOwnershipForm?.indirectPercentShareholding || "",
        directPercentVotingRights:
          values.beneficialOwnershipForm?.directPercentVotingRights || "",
        indirectPercentVotingRights:
          values.beneficialOwnershipForm?.indirectPercentVotingRights || "",
        directRightRemoveDirector:
          values.beneficialOwnershipForm?.directRightRemoveDirector || "no",
        indirectRightRemoveDirector:
          values.beneficialOwnershipForm?.indirectRightRemoveDirector || "no",
        directCompanyControlRight:
          values.beneficialOwnershipForm?.directCompanyControlRight || "no",
        indirectCompanyControlRight:
          values.beneficialOwnershipForm?.indirectCompanyControlRight || "no",
        dateOfBecomingBO:
          values.beneficialOwnershipForm?.dateOfBecomingBO || "",
        sourceOfBOInformation:
          values.beneficialOwnershipForm?.sourceOfBOInformation || "",
        resolvedFields: values.beneficialOwnershipForm?.resolvedFields || [],
      };

      const beneficialOwnershipChanges = {};
      Object.keys(updatedBeneficialOwnership).forEach((key) => {
        if (
          String(currentBeneficialOwnership[key]) !==
          String(updatedBeneficialOwnership[key])
        ) {
          beneficialOwnershipChanges[key] = {
            from: currentBeneficialOwnership[key],
            to: updatedBeneficialOwnership[key],
          };
        }
      });

      const beneficialOwnershipDetails = {
        // Current beneficial ownership details (before changes)
        currentDetails: {
          directPercentShareholding: Number(
            currentBeneficialOwnership.directPercentShareholding || 0
          ),
          indirectPercentShareholding: Number(
            currentBeneficialOwnership.indirectPercentShareholding || 0
          ),
          directPercentVotingRights: Number(
            currentBeneficialOwnership.directPercentVotingRights || 0
          ),
          indirectPercentVotingRights: Number(
            currentBeneficialOwnership.indirectPercentVotingRights || 0
          ),
          directRightRemoveDirector:
            currentBeneficialOwnership.directRightRemoveDirector,
          indirectRightRemoveDirector:
            currentBeneficialOwnership.indirectRightRemoveDirector,
          directCompanyControlRight:
            currentBeneficialOwnership.directCompanyControlRight,
          indirectCompanyControlRight:
            currentBeneficialOwnership.indirectCompanyControlRight,
          dateOfBecomingBO: currentBeneficialOwnership.dateOfBecomingBO
            ? new Date(currentBeneficialOwnership.dateOfBecomingBO)
            : null,
          sourceOfBOInformation:
            currentBeneficialOwnership.sourceOfBOInformation,
          maxPercentShareholding:
            currentBeneficialOwnership.maxPercentShareholding,
          maxPercentVotingRights:
            currentBeneficialOwnership.maxPercentVotingRights,
        },
        // Updated beneficial ownership details (after changes)
        updatedDetails: {
          directPercentShareholding: Number(
            updatedBeneficialOwnership.directPercentShareholding || 0
          ),
          indirectPercentShareholding: Number(
            updatedBeneficialOwnership.indirectPercentShareholding || 0
          ),
          directPercentVotingRights: Number(
            updatedBeneficialOwnership.directPercentVotingRights || 0
          ),
          indirectPercentVotingRights: Number(
            updatedBeneficialOwnership.indirectPercentVotingRights || 0
          ),
          directRightRemoveDirector:
            updatedBeneficialOwnership.directRightRemoveDirector,
          indirectRightRemoveDirector:
            updatedBeneficialOwnership.indirectRightRemoveDirector,
          directCompanyControlRight:
            updatedBeneficialOwnership.directCompanyControlRight,
          indirectCompanyControlRight:
            updatedBeneficialOwnership.indirectCompanyControlRight,
          dateOfBecomingBO: updatedBeneficialOwnership.dateOfBecomingBO
            ? new Date(updatedBeneficialOwnership.dateOfBecomingBO)
            : null,
          sourceOfBOInformation:
            updatedBeneficialOwnership.sourceOfBOInformation,
          maxPercentShareholding:
            updatedBeneficialOwnership.maxPercentShareholding,
          maxPercentVotingRights:
            updatedBeneficialOwnership.maxPercentVotingRights,
        },
        // Track which fields have changed
        changedFields: beneficialOwnershipChanges,
        // Timestamp when beneficial ownership was last updated
        lastUpdated: new Date(),
        // User who made the beneficial ownership changes
        updatedBy: currentUser?._id,
        // Effective date of the beneficial ownership changes
        effectiveDate: transactionDataToUse?.effectiveDate
          ? new Date(transactionDataToUse.effectiveDate)
          : new Date(),
      };

      // Detect if this is a share transfer
      const isShareTransfer = initiatedProcessType === "shareTransfer";

      let finalAllocatedShares = allocatedShares;

      // Get consideration from existing allocation if it exists
      const existingConsideration = existing.find(
        (a) => String(a?.officialId) === String(officialId)
      )?.consideration;
      const { allocations } = transactionDataToUse;
      const foundAllocation = allocations.find(
        (alloc) => alloc.officialId.toString() === officialId.toString()
      );

      const isTransferor =
        foundAllocation?.isTransferor ??
        transaction?.shareCapitalChanges?.some((sc) => {
          if (sc?.remainder === undefined || sc?.remainder === null)
            return false;
          return Number(sc.remainder) !== 0;
        }) ??
        true;

      const newAllocation = {
        ...foundAllocation,
        officialId,
        name: fullNameForAlloc,
        currentShares,
        votingRights,
        allocatedShares: finalAllocatedShares, // Use finalAllocatedShares which accounts for transfer logic
        proportion,
        paidUpShares: Number(transactionDataToUse?.paidUpShares || 0),
        sharePrice: Number(transactionDataToUse?.sharePrice || 0),
        sharePremium: Number(transactionDataToUse?.sharePremium || 0),
        // Include beneficial ownership details within the allocation
        beneficialOwnershipDetails: beneficialOwnershipDetails,
        // Add transfer-specific flags and consideration
        ...(isShareTransfer && {
          type: "transfer",
          isTransferor,
          ...(existingConsideration && {
            consideration: existingConsideration,
          }),
        }),
      };

      // Remove legacy beneficialOwnership field if present to avoid CastErrors
      if (newAllocation.beneficialOwnership) {
        delete newAllocation.beneficialOwnership;
      }

      if (idx >= 0) {
        newAllocations[idx] = newAllocation;
      } else {
        newAllocations.push(newAllocation);
      }

      // Clean up all allocations to remove legacy fields to prevent CastErrors
      newAllocations = newAllocations.map((alloc) => {
        const { beneficialOwnership, ...rest } = alloc;
        return rest;
      });

      // Calculate updated remainder
      const newAllocatedTotal = newAllocations.reduce(
        (sum, a) => sum + Number(a.allocatedShares || 0),
        0
      );
      const onOffer = Number(change?.sharesOnOffer || 0);
      const updatedRemainder = Math.max(0, onOffer - newAllocatedTotal);

      // Update the change with new allocations
      const updatedChange = {
        ...change,
        allocations: newAllocations,
        remainder: updatedRemainder,
      };

      const updatedShareCapitalChanges = changes.map((c, i) =>
        i === targetIdx ? updatedChange : c
      );

      const payload = {
        companyId,
        documentId: transaction?._id,
        formName: "initiateTransactionChange",
        shareCapitalChanges: updatedShareCapitalChanges,
      };

      const persistResult = await persistShareCapitalChanges(
        updatedShareCapitalChanges
      );

      // Save beneficial ownership changes to Redux for tracking
      dispatch(
        saveBeneficialOwnershipChanges({
          companyId,
          officialId,
          documentId: transaction?._id,
          beneficialOwnershipDetails: newAllocation.beneficialOwnershipDetails,
        })
      );

      dispatch(
        clearManualAllocation({
          companyId,
          officialId,
          transactionId: transaction?._id,
        })
      );

      dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId,
          params: { transactionType: "MANUAL" },
        })
      );

      // Calculate and display shareholding percentage changes
      const percentageChanges = calculateAndComparePercentages(
        updatedShareCapitalChanges
      );

      if (typeof onAllocationSuccess === "function") {
        onAllocationSuccess({
          allocations: newAllocations,
          remainder: updatedRemainder,
          percentageChanges, // Pass the changes to parent if needed
        });
      } else if (row?.toggleRowExpanded) {
        row.toggleRowExpanded();
      }

      // Display transfer-specific toast messages
      let successMessage;
      if (isShareTransfer) {
        if (isTransferor) {
          successMessage = "Share transferor details saved successfully.";
        } else if (!isTransferor) {
          successMessage = "Share transferee details saved successfully.";
        } else {
          successMessage = "Share transfer processed successfully.";
        }
      } else if (boOnlyView) {
        successMessage = "Beneficial ownership updated successfully.";
      } else {
        successMessage = "Manual allotment processed successfully.";
      }

      toast("success", successMessage);

      if (boOnlyView && typeof onBOUpdateSuccess === "function") {
        onBOUpdateSuccess();
      }
    } catch (error) {
      console.error("Failed to process allotment:", error);
      toast("error", error.message || "Failed to process allotment.");
    } finally {
      setIsProcessingAllotment(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    const localKey = `allotment_${companyId}_${officialId}_${transaction._id}`;
    localStorage.removeItem(localKey);
    setShareAllocationComplete(false);
    setLocalTransactionData(null);
    setShowAllocationForm(true); // Reset to show form
    if (row?.toggleRowExpanded) row.toggleRowExpanded();
  };

  // UI
  if (!isManualTransaction) {
    return (
      <tr className="bg-[#EDF0f5]">
        <td colSpan={colSpan} className="px-9">
          <div className="text-center text-gray-500 py-6">
            No valid manual transaction for allocation.
          </div>
        </td>
      </tr>
    );
  }

  const renderBody = () => {
    switch (initiatedProcessType) {
      case "privateWithMoreThanOneClass":
      case "PVTwithOneClass":
      case "existingAuth":
      case "unallotedShares": {
        return (
          <Formik
            initialValues={{
              shareAllocationForm: {},
              beneficialOwnershipForm: {
                maxPercentShareholding: 0,
                maxPercentVotingRights: 0,
                indirectPercentShareholding: 0,
                indirectPercentVotingRights: 0,
                directPercentShareholding: "",
                directPercentVotingRights: "",
                directRightRemoveDirector: "no",
                indirectRightRemoveDirector: "no",
                directCompanyControlRight: "no",
                indirectCompanyControlRight: "no",
                dateOfBecomingBO: "",
                sourceOfBOInformation: "",
                resolvedFields: [],
              },
            }}
            onSubmit={handleProcessAllotment}
          >
            <Form>
              {!boOnlyView && (
                <div className="mb-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                  <div className="px-4 py-6 sm:p-6">
                    <MainShareAllocationComponent
                      currentOfficialId={officialId}
                      isCorporate={[
                        "Local Company",
                        "Foreign Company",
                        "Other e.g Parastatals",
                      ].includes(idType)}
                      resolvedFields={[]}
                      disabled={
                        mode.viewing ||
                        (disableForm && !isAccessAllowed && mode.editing)
                      }
                      autoOpenSelection={showAllocationForm} // Control form visibility
                      editableShareAlloc={
                        showAllocationForm && !shareAllocationComplete
                          ? {
                              classOfShares: shareChange?.classOfShares || "",
                            }
                          : null
                      } // Let MainShareAllocationComponent handle edit data from table
                      isInManualUpdate={true}
                      remainderAmounts={remainderAmounts}
                      transactions={transaction}
                      row={row}
                      onSubmissionComplete={handleShareAllocationComplete}
                      onEditManualAllocation={
                        mode.viewing ? null : handleEditAllocation
                      }
                      onClearManualAllocation={() => {
                        setShowAllocationForm(true);
                        setShareAllocationComplete(false);
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="mt-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                <div className="px-4 py-6 sm:p-6">
                  <h2 className="mb-1 text-lg font-medium leading-6 text-gray-900">
                    Beneficial Ownership
                  </h2>
                  <Field
                    name="beneficialOwnershipForm"
                    component={OfficialsBeneficialOwnershipComponent}
                    currentOfficialId={officialId}
                    isCorporate={[
                      "Local Company",
                      "Foreign Company",
                      "Other e.g Parastatals",
                    ].includes(idType)}
                    resolvedFields={[]}
                    disabled={mode.viewing} // All fields editable
                    isReportable={isReportable}
                    isInManualUpdate={true}
                    transactions={transaction}
                    row={row}
                    percentageChanges={percentageChanges}
                    originalPercentages={originalPercentages}
                  />
                </div>
              </div>

              {/* Shareholding Percentage Changes Alert - Don't show for share transfers or during active allocation */}
              {initiatedProcessType !== "shareTransfer" &&
                (!showAllocationForm || boOnlyView) && (
                  <div className="px-6">
                    <ShareholderPercentageAlert
                      percentageChanges={percentageChanges}
                      isVisible={showPercentageAlert}
                    />
                  </div>
                )}

              {!mode.viewing && (
                <div className="flex justify-end px-6 pb-6">
                  <Button
                    onClick={handleCancel}
                    className="flex items-center px-4 py-2 font-semibold text-white rounded bg-gray-500 mr-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isSubmitting || isProcessingAllotment}
                    loadingText="Processing..."
                    className="flex items-center px-4 py-2 font-semibold text-white rounded bg-sflPrimary disabled:cursor-not-allowed"
                    disabled={!shareAllocationComplete}
                  >
                    {boOnlyView
                      ? "Update Beneficial Ownership"
                      : "Process Allotment"}
                  </Button>
                </div>
              )}
            </Form>
          </Formik>
        );
      }

      case "shareTransfer": {
        // Check if user is transferor (remainder not zero)
        const isTransferor =
          transaction?.shareCapitalChanges?.some((sc) => {
            if (sc?.remainder === undefined || sc?.remainder === null)
              return false;
            return Number(sc.remainder) !== 0;
          }) ?? true;

        // Check if user has fully paid up shares for any of the classes being transferred
        const hasFullyPaidUpShares = (() => {
          if (
            !isTransferor ||
            !row?.original?.sharesAllotted ||
            !transaction?.shareCapitalChanges
          ) {
            return false;
          }

          // Check each class of shares in the transaction
          return transaction.shareCapitalChanges.some((sc) => {
            const shareClass = row.original.sharesAllotted.find(
              (share) => share.classOfShares === sc.classOfShares
            );
            return shareClass && Number(shareClass.paidUpShares || 0) > 0;
          });
        })();

        // If transferor has no fully paid up shares, show message
        if (isTransferor && !hasFullyPaidUpShares) {
          return (
            <div className="px-6 py-8 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                No Fully Paid Up Shares
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                You cannot transfer shares because you do not have any fully
                paid up shares for the class being transferred.
              </p>
              <div className="mt-6">
                <Button
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Close
                </Button>
              </div>
            </div>
          );
        }

        return (
          <Formik
            initialValues={{
              shareAllocationForm: {},
              beneficialOwnershipForm: {
                maxPercentShareholding: 0,
                maxPercentVotingRights: 0,
                indirectPercentShareholding: 0,
                indirectPercentVotingRights: 0,
                directPercentShareholding: "",
                directPercentVotingRights: "",
                directRightRemoveDirector: "no",
                indirectRightRemoveDirector: "no",
                directCompanyControlRight: "no",
                indirectCompanyControlRight: "no",
                dateOfBecomingBO: "",
                sourceOfBOInformation: "",
                resolvedFields: [],
              },
            }}
            onSubmit={handleProcessAllotment}
          >
            <Form>
              {!boOnlyView && (
                <div className="mb-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                  <div className="px-4 py-6 sm:p-6">
                    <MainShareAllocationComponent
                      currentOfficialId={officialId}
                      isCorporate={[
                        "Local Company",
                        "Foreign Company",
                        "Other e.g Parastatals",
                      ].includes(idType)}
                      resolvedFields={[]}
                      disabled={
                        mode.viewing ||
                        (disableForm && !isAccessAllowed && mode.editing)
                      }
                      autoOpenSelection={showAllocationForm} // Control form visibility
                      editableShareAlloc={
                        showAllocationForm && !shareAllocationComplete
                          ? {
                              classOfShares: shareChange?.classOfShares || "",
                            }
                          : null
                      } // Let MainShareAllocationComponent handle edit data from table
                      isInManualUpdate={true}
                      remainderAmounts={remainderAmounts}
                      transactions={transaction}
                      row={row}
                      onSubmissionComplete={handleShareAllocationComplete}
                      onEditManualAllocation={
                        mode.viewing ? null : handleEditAllocation
                      }
                      onClearManualAllocation={() => {
                        setShowAllocationForm(true);
                        setShareAllocationComplete(false);
                      }}
                      initiatedProcessType={initiatedProcessType}
                    />
                  </div>
                </div>
              )}

              <div className="mt-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                <div className="px-4 py-6 sm:p-6">
                  <h2 className="mb-1 text-lg font-medium leading-6 text-gray-900">
                    Beneficial Ownership
                  </h2>
                  <Field
                    name="beneficialOwnershipForm"
                    component={OfficialsBeneficialOwnershipComponent}
                    currentOfficialId={officialId}
                    isCorporate={[
                      "Local Company",
                      "Foreign Company",
                      "Other e.g Parastatals",
                    ].includes(idType)}
                    resolvedFields={[]}
                    disabled={mode.viewing} // All fields editable
                    isReportable={isReportable}
                    isInManualUpdate={true}
                    transactions={transaction}
                    row={row}
                    percentageChanges={percentageChanges}
                    originalPercentages={originalPercentages}
                  />
                </div>
              </div>

              {/* Shareholding Percentage Changes Alert - Don't show for share transfers */}
              {initiatedProcessType !== "shareTransfer" && (
                <div className="px-6">
                  <ShareholderPercentageAlert
                    percentageChanges={percentageChanges}
                    isVisible={showPercentageAlert}
                  />
                </div>
              )}

              {!mode.viewing && (
                <div className="flex justify-end px-6 pb-6">
                  <Button
                    onClick={handleCancel}
                    className="flex items-center px-4 py-2 font-semibold text-white rounded bg-gray-500 mr-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isSubmitting || isProcessingAllotment}
                    loadingText="Processing..."
                    className="flex items-center px-4 py-2 font-semibold text-white rounded bg-sflPrimary disabled:cursor-not-allowed"
                    disabled={!shareAllocationComplete}
                  >
                    {(() => {
                      if (boOnlyView) return "Update Beneficial Ownership";

                      // Detect if this is a share transfer
                      const isShareTransfer =
                        initiatedProcessType === "shareTransfer";
                      if (!isShareTransfer) return "Save Allocation";

                      // Determine button label based on remainder
                      const allRemainderZero =
                        transaction?.shareCapitalChanges?.every((sc) => {
                          if (
                            sc?.remainder === undefined ||
                            sc?.remainder === null
                          )
                            return false;
                          return Number(sc.remainder) === 0;
                        }) ?? false;
                      return allRemainderZero
                        ? "Set Share Transferee"
                        : "Set Share Transferor";
                    })()}
                  </Button>
                </div>
              )}
            </Form>
          </Formik>
        );
      }

      default:
        return (
          <div className="text-center text-gray-500 py-6">
            Process type not supported: {initiatedProcessType}
          </div>
        );
    }
  };

  return (
    <tr className="bg-white">
      <td colSpan={colSpan} className="px-9">
        <div className="relative">{renderBody()}</div>
      </td>
    </tr>
  );
};

export default ShareChangesSubRow;
